import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Flag, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '@/components/ui/Toast';
import { reviewService } from '@/services/review.service';

interface ReportReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  reviewId: string;
  onSuccess?: () => void;
}

const REPORT_REASONS = [
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'spam', label: 'Spam or advertising' },
  { value: 'fake', label: 'Fake or misleading review' },
  { value: 'offensive', label: 'Offensive language' },
  { value: 'other', label: 'Other' },
];

export default function ReportReviewModal({
  isOpen,
  onClose,
  reviewId,
  onSuccess,
}: ReportReviewModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');

  const reportMutation = useMutation({
    mutationFn: (reason: string) => reviewService.reportReview(reviewId, { reason }),
    onSuccess: () => {
      toast.success('Review reported successfully');
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to report review');
    },
  });

  const handleClose = () => {
    setSelectedReason('');
    setAdditionalDetails('');
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedReason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    const reason = selectedReason === 'other' && additionalDetails
      ? `Other: ${additionalDetails}`
      : REPORT_REASONS.find(r => r.value === selectedReason)?.label || selectedReason;

    reportMutation.mutate(reason);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Flag size={20} className="text-red-500" />
                  <h3 className="font-semibold text-gray-900">Report Review</h3>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="px-5 py-4">
                {/* Warning */}
                <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                  <AlertTriangle size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-orange-800">
                    Please only report reviews that violate our community guidelines. False reports may result in action on your account.
                  </p>
                </div>

                {/* Reason Selection */}
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Why are you reporting this review?
                </label>
                <div className="space-y-2">
                  {REPORT_REASONS.map((reason) => (
                    <label
                      key={reason.value}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedReason === reason.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reportReason"
                        value={reason.value}
                        checked={selectedReason === reason.value}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{reason.label}</span>
                    </label>
                  ))}
                </div>

                {/* Additional Details (shown when "Other" is selected) */}
                {selectedReason === 'other' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please provide more details
                    </label>
                    <textarea
                      value={additionalDetails}
                      onChange={(e) => setAdditionalDetails(e.target.value)}
                      placeholder="Describe the issue..."
                      rows={3}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                      maxLength={500}
                    />
                    <span className="text-xs text-gray-400">{additionalDetails.length}/500</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!selectedReason || reportMutation.isPending}
                  className="flex-1 py-2.5 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reportMutation.isPending ? 'Reporting...' : 'Report Review'}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
